import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { type ProductEntity, type SprintEntity } from '@data/utils/types';
import { fetchProduct } from '@data/api/products';
import { fetchProductIncidents } from '@data/api/incidents';
import * as queryKeys from '@data/constants/keys';
import { limitConcurrency } from '@app/utils/helpers/limitConcurrency';

import { versionsFor } from '../../products/productDetail/productVersions';
import { todayKey } from '../../products/productDetail/useProductVersions';
import { rateIn, teamAt } from '../../products/productDetail/budget';
import { versionTeams } from '../../products/productDetail/useVersionTeams';
import { productStoreQuery } from '../../products/productDetail/useProductStore';
import { readServiceHours } from '../../products/productDetail/serviceLevels';
import { productReportsQuery } from '../../insights/productReports';
import { reportsBySprint } from '../../insights/sprintReports';
import { incidentFiguresBySprint, type IncidentSprintFigures } from './incidentFigures';

const READS_AT_ONCE = 4;

/** Every product's incident time per sprint, off the same per-product read the cost card prices. */
export const useClientIncidentCosts = (
  products: { id: string }[],
  sprints: SprintEntity[],
): IncidentSprintFigures & { isLoading: boolean; isError: boolean } => {
  const queue = useMemo(() => limitConcurrency(READS_AT_ONCE), []);
  const productQueries = useQueries({
    queries: products.map(product => ({
      queryKey: [queryKeys.PRODUCT, product.id],
      queryFn: () => queue(() => fetchProduct(product.id)),
      staleTime: 60_000,
    })),
  });
  const storeQueries = useQueries({
    queries: products.map(product => {
      const query = productStoreQuery(product.id);
      return { ...query, queryFn: () => queue(query.queryFn) };
    }),
  });
  const incidentQueries = useQueries({
    queries: products.map(product => ({
      queryKey: [queryKeys.PRODUCT_INCIDENTS, product.id],
      queryFn: () => queue(() => fetchProductIncidents(product.id)),
    })),
  });
  const reportQueries = useQueries({
    queries: products.map(product => productReportsQuery(product.id, sprints, queue)),
  });

  const all = [...productQueries, ...storeQueries, ...incidentQueries, ...reportQueries];
  const stamps = all.map(query => query.dataUpdatedAt).join(',');
  const figures = useMemo(
    () =>
      incidentFiguresBySprint(
        products.map((product, index) => {
          const teams = versionTeams(
            productQueries[index]?.data as ProductEntity | undefined,
            versionsFor(product.id, todayKey()),
          );
          return {
            id: product.id,
            rateAt: (day: string, memberId: string) => rateIn(teamAt(teams, day), memberId),
            serviceAt: (day: string) => readServiceHours(product.id, teamAt(teams, day)?.version.number),
            incidents: incidentQueries[index]?.data?.items ?? [],
            reportsBySprint: reportsBySprint(reportQueries[index]?.data?.items ?? [], sprints, { incidentWork: true }),
          };
        }),
      ),
    // Keyed on when the data last changed: the query objects are new every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products.map(product => product.id).join(), sprints, stamps],
  );

  return {
    ...figures,
    isLoading: all.some(query => query.isLoading),
    isError: all.some(query => query.isError),
  };
};
